<template lang="pug">
component.study(
  ref='template',
  :is='currentComponent',
  :studyTitle='studyTitle',
  :currentTime='currentTime',
  :remainingTime='remainingTime',
  :isChangingProblem='isChangingProblem',
  :isVertical='isVertical',
  :isLastProblem='isLastProblem',
  :isFirstTime='isFirstTime',
  :nextButtonEvent='nextButtonEvent',
  :prevButtonEvent='prevButtonEvent',
  :audioCommentImageUrl='audioCommentImageUrl',
  :audioViewSetting='audioViewSetting',
  :audioInfo='audioInfo',
  :audioStartedEvent='audioStartedEvent',
  :questionMemo='questionMemo',
  :drawEndEvent='drawEndEvent',
  :problems='problems',
  :currentProblem='currentProblem',
  :currentQuestion='currentQuestion',
  :mathKeyboardName='mathKeyboardName',
  :selectQuestionEvent='selectQuestionEvent',
  :notAnsweredNumbers='notAnsweredNumbers',
  :studyConfirmEvent='studyConfirmEvent',
  :studyCompleteEvent='studyCompleteEvent',
  :studyEndEvent='studyEndEvent',
  :moveReturnEvent='moveReturnEvent'
)
  template(#userUidTitle) 個人ID
  template(#userNameTitle) ユーザ名
  template(#returnButtonText) もどる
  template(#currentTimeLabel) 実施時間：
  template(#remainingTimeLabel) 残り時間：
  template(#problemNumberBeforeLabel) 【問題】〈
  template(#problemNumberAfterLabel) 〉
  template(#questionCheckboxLabel) あとで見直す
  template(#tegakiResultTitle) 記入結果：
  template(#tegakiRecognizeButtonLabel) 読み取り
  template(#answerNextButtonLabel) 次の問題へ
  template(#answerCompleteButtonLabel) 終了する
  template(#answerCompleteProblemButtonLabel) 次の大問へ
  template(#menuProblemNumberBeforeLabel) 大問
  template(#menuCompleteButtonLabel) 試験を終了して提出
  template(#menuCompleteProblemButtonLabel) 次の大問へ
  template(#bulkNextButtonLabel) 次の問題へ
  template(#bulkCompleteButtonLabel) 試験を終了して提出
  template(#studyConfirmTitle) すべて解答済みですか？
  template(#notAnsweredLabel) が未回答です。
  template(#studyConfirmText1a) 「はい」を押すとテストが終了します。<br>終了したテストに戻ることはできません。
  template(#studyConfirmText1b) 「はい」を押すとここまで解いた問題に<br>戻ることはできません。
  template(#studyConfirmText2) 問題をまだ解く、見直しをする場合は<br>「いいえ」を押してテストに戻ってください。
  template(#submitText) はい
  template(#hideText) いいえ
  template(#studySuspendTitle) テストを中断します
  template(#studySuspendText1) このボタンは提出ボタンではありません。<br/>「はい」を押すとテスト選択画面に移動します※結果には反映されません。
  template(#studySuspendText2) 提出する場合は「いいえ」を押して一度テストに戻り、「試験を終了して提出」を押してください。
  template(#studyCompleteTitle) 提出しました。
  template(#completeSubmitText) 結果詳細へ
</template>

<script lang="ts">
import { Component, Mixins, Ref, Watch } from 'vue-property-decorator'
import { RawLocation } from 'vue-router'
import StudyIndividual from '@/components/templates/student/StudyIndividual.vue'
import StudyBulk from '@/components/templates/student/StudyBulk.vue'
import {
  StudyProblem,
  StudyQuestion,
  StudyAudioInfoType,
  StudyQuestionData,
  StudyQuestionMemoType,
  StudyType,
  StudyTypeCode,
} from '@/models/studyModel'
import { DrawingDataType } from '@/models/drawingDataModel'
import { AudioViewSetting } from '@/const/AudioViewTypes'
import { StudyUtils } from '@/utils/StudyUtils'

import DrillApi from '@/mixins/api/DrillApi'
import { ApiResponseLoadProblems } from '@/models/apiResponses/drill'

/**
 * 学習画面
 */
@Component({
  components: {
    StudyIndividual,
    StudyBulk,
  },
})
export default class Study extends Mixins(DrillApi) {
  // ドリル結果IDをクエリーより取得
  private get resultDrillId(): number {
    return Number(this.$route.query.resultDrillId)
  }

  // テンプレートへのアクセス
  @Ref() template!: StudyIndividual

  /**
   * 問題切替中フラグ
   * 主に解答欄のコンポーネントの再描画を行う為、一時ローディングフラグを立ててコンポーネントを破棄する
   */
  private isChangingProblem = false

  // 学習画面表示タイプ
  private studyType: StudyType = null

  // 縦書きフラグ
  private isVertical = false

  // 最終大問フラグ
  private isLastProblem = false

  // テスト名
  private studyTitle = ''

  // 時間強制終了フラグ
  private endConditionIsTime = false

  // テスト開始からトータルの経過時間(秒)
  private durationTotal = 0

  // テストの総合制限時間（秒）
  private timeLimitTotal = 0

  // 音声コメント画像のURL
  private audioCommentImageUrl = ''

  // 音声設定
  private audioViewSetting: AudioViewSetting | null = null

  // 数式キーボード名称
  private mathKeyboardName = ''

  // 全問題情報
  private problems: StudyProblem[] = []

  // 音声情報
  private audioInfos: StudyAudioInfoType[] = []

  // 画面メモ情報
  private questionMemos: StudyQuestionMemoType[] = []

  // 完了確認モーダルに表示する未入力問題番号
  private notAnsweredNumbers: string[] = []

  // 現在のページの経過時間（秒）
  private currentDuration = 0

  // 経過時間タイマー(setIntervalの戻り値はnumber型)
  private currentDurationTimer?: number

  // 時間切れ状態保持フラグ
  private isTimeUp = false

  // 初回フラグ
  private isFirstTime = false

  // 学習後の遷移先
  private studyEndMoveLocation: RawLocation | null = null

  // 選択中の情報
  private currentProblem: StudyProblem | null = null
  private currentQuestion: StudyQuestion | null = null
  private audioInfo: StudyAudioInfoType | null = null
  private questionMemo: StudyQuestionMemoType | null = null

  // 使用コンポーネント
  private get currentComponent(): string {
    switch (this.studyType) {
      case StudyTypeCode.QUESTION_AND_ANSWER:
        // 問題＆解答表示形式の場合は、個別表示
        return 'StudyIndividual'
      case StudyTypeCode.ANSWER_ONLY_ALL:
      case StudyTypeCode.ANSWER_ONLY_PARTS:
        // 問題毎に全設問表示または全問表示の場合は、一括表示
        return 'StudyBulk'
    }
    return ''
  }

  // 実施時間
  private get currentTime(): string {
    return `${(this.timeLimitTotal / 60).toFixed()}分`
  }

  // 残り時間
  private get remainingTime(): string {
    const remaining = this.timeLimitTotal - this.durationTotal - this.currentDuration
    // 制限時間を過ぎた場合は0分0秒表示
    if (remaining < 0) return '0分0秒'
    return `${((remaining / 60) | 0).toFixed()}分${(remaining % 60).toFixed()}秒`
  }

  // 入力値に変更があった場合は入力済み情報チェック
  @Watch('problems', { deep: true })
  private updateQuestion(): void {
    this.setInputted()
  }

  // 経過時間を監視しタイムアップを通知する
  @Watch('currentDuration')
  private onChangeDuration(): void {
    this.alertTimeUp()
  }

  /**
   * [Vue] 画面読み込み時：初期処理
   */
  private async mounted(): Promise<void> {
    this.$loading.start()
    // 学習ドリルに紐付く出題大問・問題情報一覧取得
    try {
      // キャプチャー用にBase64画像を含めて取得
      const apiResponse = await this.loadProblemsApi(this.resultDrillId)
      await this.initStudyData(apiResponse)
    } finally {
      this.$loading.complete()
    }
  }

  /**
   * 対象問題読み込み
   */
  private async initStudyData(apiResponse: ApiResponseLoadProblems): Promise<void> {
    // 学習表示タイプをセット
    this.studyType = StudyUtils.getStudyType(apiResponse)
    // 縦書きフラグをセット
    this.isVertical = StudyUtils.getIsVertical(apiResponse)
    // 最終大問フラグをセット
    this.isLastProblem = StudyUtils.getIsLastProblem(apiResponse)
    // テスト名をセット
    this.studyTitle = StudyUtils.buildStudyTitle(apiResponse)
    // 時間強制終了フラグをセット
    this.endConditionIsTime = StudyUtils.getEndConditionIsTime(apiResponse)
    // 経過時間をセット
    this.durationTotal = StudyUtils.getDurationTotal(apiResponse)
    // 制限時間をセット
    this.timeLimitTotal = StudyUtils.getTimeLimitTotal(apiResponse)
    // 音声コメント画像URLをセット
    this.audioCommentImageUrl = StudyUtils.getAudioCommentImageUrl(apiResponse)
    // 音声設定をセット
    this.audioViewSetting = StudyUtils.getAudioViewSetting(apiResponse)
    // 数式キーボード名をセット
    this.mathKeyboardName = StudyUtils.getMathKeyboardName(apiResponse)
    // 初回フラグをセット
    this.isFirstTime = StudyUtils.getIsFirstTime(apiResponse)
    // 学習後の遷移先をセット
    this.studyEndMoveLocation = StudyUtils.buildStudyEndMoveLocation(apiResponse)
    // 問題情報をセット
    this.problems = StudyUtils.buildProblems(apiResponse)

    await this.$nextTick()
    if (this.template) {
      this.template.setResultDrillId(this.resultDrillId)
    } else {
      console.warn('[Study.vue] StudyIndividualコンポーネントがまだマウントされていません')
    }

    // 音声情報をセット
    this.audioInfos = StudyUtils.buildAudioInfos(apiResponse)
    // 画像メモ情報をセット
    this.questionMemos = StudyUtils.buildQuestionMemos(apiResponse)

    // 経過時間タイマーをセット
    this.setDurationTimer()

    // 初期選択問題をセット
    await this.initCurrent()
  }

  // 経過時間タイマーをセット
  private setDurationTimer(): void {
    this.currentDurationTimer = window.setInterval(() => {
      this.currentDuration += 1
    }, 1000)
  }

  // 初期選択問題をセット
  private async initCurrent(): Promise<void> {
    const problem = this.problems[0]
    const question = problem.questions[0]
    switch (this.studyType) {
      case StudyTypeCode.QUESTION_AND_ANSWER:
        // 問題＆解答表示形式の場合は、問題、設問を選択
        await this.setCurrent(problem, question)
        break
      case StudyTypeCode.ANSWER_ONLY_PARTS:
        // 問題毎に全設問表示の場合は、問題を選択
        await this.setCurrent(problem)
        break
      case StudyTypeCode.ANSWER_ONLY_ALL:
        // 全問表示の場合は選択不要
        return
    }
  }

  // 選択中の問題、設問を変更
  private async setCurrent(problem: StudyProblem, question?: StudyQuestion): Promise<void> {
    // キャプチャーを先に取得（画面が変わる前）
    let captureData: string | null = null
    if (this.template && this.currentProblem && this.currentQuestion) {
      try {
        captureData = await this.template.captureScreen()
      } catch (error) {
        console.error('[setCurrent] キャプチャー取得エラー:', error)
      }
    }

    this.isChangingProblem = true

    // 現ページの経過時間を加算
    this.durationTotal += this.currentDuration
    if (this.currentProblem) {
      this.currentProblem.duration += this.currentDuration
    }
    if (this.currentQuestion) {
      this.currentQuestion.duration += this.currentDuration
    }

    // 直前の入力内容を保存する（キャプチャーデータを渡す）
    switch (this.studyType) {
      case StudyTypeCode.QUESTION_AND_ANSWER:
        // 問題＆解答表示形式の解答送信
        await this.saveQuestionStudyAnswerWithProvidedCapture(captureData)
        break
      case StudyTypeCode.ANSWER_ONLY_PARTS:
        // 問題毎に全設問表示の解答送信
        await this.saveProblemStudyAnswer()
        break
    }

    // 問題を変更
    this.currentProblem = problem
    // 設問も指定された場合は設問、音声情報、メモ情報を変更する
    if (question) {
      this.currentQuestion = question
      // 変更後の問題、設問に紐付く音声情報を変更
      this.audioInfo = this.getAudioInfo(problem, question)
      // 設問に紐付くメモ情報を変更
      this.questionMemo = this.getQuestionMemo(question)
    }
    // 現ページの経過時間を0にする
    this.currentDuration = 0

    // 問題遷移時に画像を読み込む
    try {
      await this.template.loadImagesAsync(this.currentProblem, question)
    } catch (error) {
      console.error('[Study.vue] 問題遷移時の画像読み込みエラー:', error)
    }

    // コンポーネントが再利用される場合がある（出題パターンによりレンダリングが更新されない事がある）ので
    // 一連の処理後にコールバック関数でフラグを変更して強制的にレンダリングさせる
    this.$nextTick(() => (this.isChangingProblem = false))
  }

  /**
   * 設問毎の入力情報保存
   */
  private async saveQuestionStudyAnswer(): Promise<void> {
    if (this.currentProblem !== null && this.currentQuestion !== null) {
      const studyAnswer = StudyUtils.buildQuestionStudyAnswer(
        this.resultDrillId,
        this.currentProblem,
        this.currentQuestion,
        this.getQuestionMemo(this.currentQuestion)
      )

      try {
        // DOM更新を待つ（画面切り替え前の状態を確実にキャプチャー）
        await this.$nextTick()
        await new Promise((resolve) => setTimeout(resolve, 100))

        // 画面キャプチャを取得
        const captureData = await this.template.captureScreen()

        await this.saveQuestionStudyAnswerApi(studyAnswer, captureData || undefined)
      } catch (err: any) {
        if (err.response.data.status === 400) {
          alert(err.response.data.message)
        } else {
          alert('エラーが発生しました')
        }
      }
    }
  }

  /**
   * 設問毎の入力情報保存（キャプチャーデータ付き）
   */
  private async saveQuestionStudyAnswerWithProvidedCapture(captureData: string | null): Promise<void> {
    if (this.currentProblem !== null && this.currentQuestion !== null) {
      const studyAnswer = StudyUtils.buildQuestionStudyAnswer(
        this.resultDrillId,
        this.currentProblem,
        this.currentQuestion,
        this.getQuestionMemo(this.currentQuestion)
      )

      try {
        await this.saveQuestionStudyAnswerApi(studyAnswer, captureData || undefined)
      } catch (err: any) {
        if (err.response.data.status === 400) {
          alert(err.response.data.message)
        } else {
          alert('エラーが発生しました')
        }
      }
    }
  }

  /**
   * 大問毎の入力情報保存
   */
  private async saveProblemStudyAnswer(): Promise<void> {
    if (this.currentProblem !== null) {
      const studyAnswer = StudyUtils.buildProblemStudyAnswer(
        this.resultDrillId,
        this.currentProblem,
        this.getQuestionMemo(this.currentQuestion)
      )

      try {
        await this.saveQuestionStudyAnswerApi(studyAnswer)
      } catch (err: any) {
        if (err.response.data.status === 400) {
          alert(err.response.data.message)
        } else {
          alert('エラーが発生しました')
        }
      }
    }
  }

  // 音声情報を取得
  private getAudioInfo(problem: StudyProblem, question: StudyQuestion): StudyAudioInfoType {
    // 問題に紐付く音声情報が存在する場合はそれを使用する
    const problemAudioInfo = this.audioInfos.find((info: StudyAudioInfoType) => {
      return info.problemId === problem.id
    })
    if (problemAudioInfo) return problemAudioInfo

    // 設問に紐付く音声情報が存在する場合はそれを使用する
    const questionAudioInfo = this.audioInfos.find((info: StudyAudioInfoType) => {
      return info.questionId === question.id
    })
    if (questionAudioInfo) return questionAudioInfo

    // どちらにも存在しない場合は空オブジェクトを返す（再生音声なし）
    return {}
  }

  // メモ情報を取得
  private getQuestionMemo(question: StudyQuestion | null): StudyQuestionMemoType | null {
    // 解答のみ表示モード時ではメモ情報を持たせない
    if (question === null) return null

    // メモ情報一覧から設問に紐付くメモ情報を取得
    const questionMemo = this.questionMemos.find((memo: StudyQuestionMemoType) => {
      return memo.questionId === question.id
    })
    if (questionMemo) return questionMemo

    // 存在しない場合は新規のメモ情報を作成し、メモ情報一覧に追加
    const newQuestionMemo: StudyQuestionMemoType = {
      questionId: question.id,
      src: '',
      drawingData: null,
    }
    this.questionMemos.push(newQuestionMemo)
    return newQuestionMemo
  }

  // 入力済み情報設定
  // TODO: サイドナビの挙動がv1と一致しない為、学習解答APIと合わせて対応する
  private setInputted(): void {
    // 個別表示の場合のみ実行
    if (!this.currentProblem || !this.currentQuestion || !this.template) return

    const index = this.template.inputtedQuestionIds.indexOf(this.currentQuestion.id)

    if (this.choiceInputted(this.currentQuestion) || this.questionInputted(this.currentQuestion)) {
      // 入力済みの場合、inputtedQuestionIdsにIDを追加
      if (index === -1) this.template.inputtedQuestionIds.push(this.currentQuestion.id)
    } else {
      // 未入力の場合、inputtedQuestionIdsから削除
      if (index !== -1) this.template.inputtedQuestionIds.splice(index, 1)
    }
  }

  // 選択系入力チェック
  private choiceInputted(question: StudyQuestion): boolean {
    // TODO: サイドナビの挙動がv1と一致しない為、学習解答APIと合わせて対応する
    if (question.questionChoiceData) {
      return question.questionChoiceData.inputValue.length > 0
    }

    return false
  }

  // 入力系入力チェック
  private questionInputted(question: StudyQuestion): boolean {
    // TODO: サイドナビの挙動がv1と一致しない為、学習解答APIと合わせて対応する
    if (question.questionDatas) {
      return question.questionDatas.every((data: StudyQuestionData) => {
        return data.inputValue !== ''
      })
    }

    return false
  }

  // タイムアップ通知
  private alertTimeUp(): void {
    if (!this.isTimeUp && this.currentDuration >= this.timeLimitTotal - this.durationTotal) {
      clearInterval(this.currentDurationTimer)
      this.isTimeUp = true
      alert('実施時間を超えました。')
      // 時間強制終了フラグによる時間切れ提出
      if (this.endConditionIsTime) {
        this.studyCompleteEvent()
        if (this.isLastProblem) this.studyEndEvent()
      }
    }
  }

  // 次へボタンイベント
  private async nextButtonEvent(): Promise<void> {
    if (this.isChangingProblem) return

    this.$loading.start()
    switch (this.studyType) {
      case StudyTypeCode.QUESTION_AND_ANSWER:
        // 問題＆解答表示形式の次へボタンイベント
        await this.moveNextProblemQuestion()
        break
      case StudyTypeCode.ANSWER_ONLY_PARTS:
        // 問題毎に全設問表示の次へボタンイベント
        await this.moveNextProblem()
        break
      case StudyTypeCode.ANSWER_ONLY_ALL:
        this.$loading.complete()
        // 全問表示の場合は不要
        return
    }
    this.$loading.complete()
  }

  // 問題＆解答表示形式の次へボタンイベント
  private async moveNextProblemQuestion(): Promise<void> {
    if (!this.currentProblem || !this.currentQuestion) return

    // 選択問題、設問のindex値を取得
    const currentProblemIndex = this.problems.indexOf(this.currentProblem)
    const currentQuestionIndex = this.problems[currentProblemIndex].questions.indexOf(this.currentQuestion)

    // 設問のindex値+1の情報が存在する場合は、その設問を選択中に設定
    // 存在しない場合は問題のindex値+1の問題と1問目の設問を選択中に設定
    const nextQuestion = this.problems[currentProblemIndex].questions[currentQuestionIndex + 1]
    if (nextQuestion) {
      await this.setCurrent(this.currentProblem, nextQuestion)
    } else {
      const nextProblem = this.problems[currentProblemIndex + 1]
      if (nextProblem) {
        await this.setCurrent(nextProblem, nextProblem.questions[0])
      }
    }
  }

  // 問題毎に全設問表示の次へボタンイベント
  private async moveNextProblem(): Promise<void> {
    if (!this.currentProblem) return

    // 選択問題のindex値を取得
    const currentProblemIndex = this.problems.indexOf(this.currentProblem)
    const nextProblem = this.problems[currentProblemIndex + 1]
    if (nextProblem) {
      await this.setCurrent(nextProblem)
    }
  }

  // 前へボタンイベント
  private async prevButtonEvent(): Promise<void> {
    if (this.isChangingProblem) return

    this.$loading.start()
    switch (this.studyType) {
      case StudyTypeCode.QUESTION_AND_ANSWER:
        // 問題＆解答表示形式の前へボタンイベント
        await this.movePrevProblemQuestion()
        break
      case StudyTypeCode.ANSWER_ONLY_PARTS:
        // 問題毎に全設問表示の前へボタンイベント
        await this.movePrevProblem()
        break
      case StudyTypeCode.ANSWER_ONLY_ALL:
        this.$loading.complete()
        // 全問表示の場合は不要
        return
    }
    this.$loading.complete()
  }

  // 問題＆解答表示形式の前へボタンイベント
  private async movePrevProblemQuestion(): Promise<void> {
    if (!this.currentProblem || !this.currentQuestion) return

    // 選択問題、設問のindex値を取得
    const currentProblemIndex = this.problems.indexOf(this.currentProblem)
    const currentQuestionIndex = this.problems[currentProblemIndex].questions.indexOf(this.currentQuestion)

    // 設問のindex値-1の情報が存在する場合は、その設問を選択中に設定
    // 存在しない場合は問題のindex値-1の問題と最後の設問を選択中に設定
    const prevQuestion = this.problems[currentProblemIndex].questions[currentQuestionIndex - 1]
    if (prevQuestion) {
      await this.setCurrent(this.currentProblem, prevQuestion)
    } else {
      const prevProblem = this.problems[currentProblemIndex - 1]
      if (prevProblem) {
        await this.setCurrent(prevProblem, prevProblem.questions[prevProblem.questions.length - 1])
      }
    }
  }

  // 問題毎に全設問表示の前へボタンイベント
  private async movePrevProblem(): Promise<void> {
    if (!this.currentProblem) return

    // 選択問題のindex値を取得
    const currentProblemIndex = this.problems.indexOf(this.currentProblem)
    const prevProblem = this.problems[currentProblemIndex - 1]
    if (prevProblem) {
      await this.setCurrent(prevProblem)
    }
  }

  // 設問選択イベント
  private async selectQuestionEvent(problemId: number, questionId: number): Promise<void> {
    if (this.isChangingProblem) return

    // 選択されたIDに該当する設問と問題を選択中に設定
    const problem = this.problems.find((problem: StudyProblem) => {
      return problem.id === problemId
    })
    if (problem) {
      const question = problem.questions.find((question: StudyQuestion) => {
        return question.id === questionId
      })

      if (question) await this.setCurrent(problem, question)
    }
  }

  // 音声再生時イベント
  private audioStartedEvent(count: number): void {
    if (this.audioInfo) {
      // 再生回数を更新
      this.audioInfo.count = count
    }
  }

  // 描画終了時イベント
  private drawEndEvent(drawData: { encodedImage: string; drawingData: DrawingDataType }): void {
    if (this.questionMemo) {
      // メモ描画情報を更新
      this.questionMemo.src = drawData.encodedImage
      this.questionMemo.drawingData = drawData.drawingData
    }
  }

  // 終了確認時イベント
  private async studyConfirmEvent(): Promise<void> {
    // 未入力アラートを設定
    this.setNotAnsweredNumbers()
  }

  // 未入力アラート設定
  private setNotAnsweredNumbers(): void {
    const notAnsweredNumbers: string[] = []
    this.problems.forEach((problem: StudyProblem) => {
      problem.questions.forEach((question: StudyQuestion) => {
        if (!this.choiceInputted(question) && !this.questionInputted(question)) {
          notAnsweredNumbers.push(`大問${problem.problemNumber}${question.questionNumberText}`)
        }
      })
    })
    this.notAnsweredNumbers = notAnsweredNumbers
  }

  // 学習完了時イベント
  private async studyCompleteEvent(): Promise<void> {
    this.$loading.start()
    if (this.currentProblem !== null && this.currentQuestion !== null) {
      const currentStudyAnswer = StudyUtils.buildQuestionStudyAnswer(
        this.resultDrillId,
        this.currentProblem,
        this.currentQuestion,
        this.getQuestionMemo(this.currentQuestion)
      )

      try {
        // DOM更新を待つ（画面切り替え前の状態を確実にキャプチャー）
        await this.$nextTick()
        await new Promise((resolve) => setTimeout(resolve, 100))

        // 画面キャプチャを取得
        const captureData = await this.template.captureScreen()

        await this.saveQuestionStudyAnswerApi(currentStudyAnswer, captureData || undefined)
      } catch (err: any) {
        if (err.response.data.status === 400) {
          alert(err.response.data.message)
        } else {
          alert('エラーが発生しました')
        }
      }
    }

    const studyAnswer = StudyUtils.buildProblemsStudyAnswer(
      this.resultDrillId,
      this.problems,
      this.getQuestionMemo(this.currentQuestion)
    )

    // 学習解答内容提出
    try {
      await this.drillCompleteApi(
        studyAnswer,
        async (apiResponse) => {
          // 最終大問ではない場合は、次の問題を表示
          if (!this.isLastProblem) await this.initStudyData(apiResponse)
        },
        async () => {
          alert('エラーが発生しました')
        }
      )
    } finally {
      this.$loading.complete()
    }
  }

  // 学習終了時イベント
  private studyEndEvent(): void {
    if (!this.studyEndMoveLocation) return

    // 学習後の遷移先に移動
    this.$router.push(this.studyEndMoveLocation)
  }

  // 中断して戻るイベント
  private moveReturnEvent(): void {
    // テスト選択画面へ戻る
    this.$router.push({ name: 'StudentStudySelection' })
  }
}
</script>
